"use client";

import React, { useState, useTransition } from "react";
import { VisitListItemProps } from "./types";
import { AvatarGroup } from "./avatar-group";
import { DOCTOR_ICON_URL } from "./constants";
import {
  formatVisitTimestamp,
  getVisitAvatarState,
  getVisitClickability,
  generateVisitTitle,
  generateVisitSubtitle,
  shouldShowNotificationCard,
  getNotificationMessage,
} from "./visit-utils";
import { defaultVisitListItemTheme } from "./themes";
import { mergeTheme } from "../utils/theme";

export interface BaseVisitItemConfig {
  /** Layout type - determines how content is arranged */
  layout: "vertical" | "horizontal";
  /** Avatar layout - determines how avatars are arranged */
  avatarLayout: "horizontal-line" | "horizontal" | "diagonal";
  /** Timestamp format preference */
  timestampFormat?: "time" | "relative" | "date";
  /** Whether to show status in top-right corner */
  showStatusBadge: boolean;
  /** Whether to show notification card */
  showNotificationCard: boolean;
  /** Whether to show subtitle */
  showSubtitle: boolean;
  /** Custom subtitle overrides */
  customSubtitles?: Partial<Record<string, string>>;
  /** Custom title generation function */
  titleGenerator?: (visit: VisitListItemProps["visit"]) => string;
  /** Custom click handler logic */
  onItemClick?: (visit: VisitListItemProps["visit"]) => void;
  /** Custom notification card renderer */
  notificationRenderer?: (
    visit: VisitListItemProps["visit"]
  ) => React.ReactNode;
}

export interface BaseVisitItemProps extends VisitListItemProps {
  config: BaseVisitItemConfig;
  /** Text component for titles */
  TitleComponent?: React.ComponentType<{
    children: React.ReactNode;
    className?: string;
  }>;
  /** Text component for subtitles */
  SubtitleComponent?: React.ComponentType<{
    children: React.ReactNode;
    className?: string;
  }>;
  /** Text component for captions */
  CaptionComponent?: React.ComponentType<{
    children: React.ReactNode;
    className?: string;
  }>;
  /** Custom avatar component override */
  AvatarComponent?: React.ComponentType<{
    firstAvatar?: string;
    firstAvatarUrl?: string;
    secondAvatar?: string;
    secondAvatarUrl?: string;
    state?: "assigned" | "not-assigned" | "loading";
    size?: "small" | "medium" | "large";
  }>;
}

export const BaseVisitItem: React.FC<BaseVisitItemProps> = ({
  visit,
  onClick,
  showNotificationCard = false,
  isClickable = true,
  className = "",
  theme,
  config,
  TitleComponent = ({ children, className }) => (
    <div className={className}>{children}</div>
  ),
  SubtitleComponent = ({ children, className }) => (
    <div className={className}>{children}</div>
  ),
  CaptionComponent = ({ children, className }) => (
    <div className={className}>{children}</div>
  ),
  AvatarComponent,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isPending, startTransition] = useTransition();

  const mergedTheme = mergeTheme(defaultVisitListItemTheme, theme);

  // Use shared utility functions
  const shouldBeClickable = isClickable && getVisitClickability(visit.status);
  const avatarState = getVisitAvatarState(
    visit.status,
    !!(visit.clinicianName || visit.clinicianFirstName)
  );
  const showNotification = shouldShowNotificationCard(
    visit,
    showNotificationCard
  );

  // Generate content using shared utilities
  const title = config.titleGenerator
    ? config.titleGenerator(visit)
    : generateVisitTitle(visit);
  const subtitle = generateVisitSubtitle(
    visit.status,
    visit.lastMessage,
    config.customSubtitles
  );
  const formattedTimestamp = config.timestampFormat
    ? formatVisitTimestamp(
        visit.timestamp,
        visit.status,
        config.timestampFormat
      )
    : undefined;

  // Get container classes based on state
  const getContainerStyles = () => {
    const classes = [mergedTheme.container?.base];

    if (shouldBeClickable) {
      classes.push(mergedTheme.container?.clickable);
    }

    if (isHovered && shouldBeClickable) {
      classes.push(mergedTheme.container?.hover);
    }

    if (isActive && shouldBeClickable) {
      classes.push(mergedTheme.container?.active);
    }

    return classes.filter(Boolean).join(" ");
  };

  // Handle interactions
  const handleClick = () => {
    if (shouldBeClickable && onClick) {
      startTransition(() => {
        onClick(visit);
      });
    }
    if (config.onItemClick) {
      config.onItemClick(visit);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (shouldBeClickable && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      handleClick();
    }
  };

  // Render notification content
  const renderNotificationContent = () => {
    if (!showNotification) return null;

    if (config.notificationRenderer) {
      return config.notificationRenderer(visit);
    }

    const message = getNotificationMessage(visit);
    if (!message) return null;

    return (
      <div
        className={`notification-card ${mergedTheme.notificationCard?.container || ""}`}
      >
        <svg
          className={mergedTheme.notificationCard?.icon}
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          {visit.status === "requested" ? (
            <path d="M6 10.17L2.83 7 1.41 8.41 6 13 15 4 13.59 2.59z" />
          ) : (
            <path d="M8 0C3.58 0 0 3.58 0 8c0 1.42.37 2.75 1.02 3.9L0 16l4.1-1.02C5.25 15.63 6.58 16 8 16c4.42 0 8-3.58 8-8s-3.58-8-8-8z" />
          )}
        </svg>
        <SubtitleComponent>{message}</SubtitleComponent>
      </div>
    );
  };

  // Render chevron for clickable items
  const renderChevron = () => {
    if (!shouldBeClickable) return null;

    if (isPending) {
      return (
        <svg
          className={`chevron-icon ${mergedTheme.chevron || ""} animate-spin`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      );
    }

    return (
      <svg
        className={`chevron-icon ${mergedTheme.chevron || ""}`}
        viewBox="0 0 17 24"
        fill="currentColor"
      >
        <path
          clipRule="evenodd"
          d="M8.46967 5.46967C8.76256 5.17678 9.23744 5.17678 9.53033 5.46967L15.5303 11.4697C15.8232 11.7626 15.8232 12.2374 15.5303 12.5303L9.53033 18.5303C9.23744 18.8232 8.76256 18.8232 8.46967 18.5303C8.17678 18.2374 8.17678 17.7626 8.46967 17.4697L13.9393 12L8.46967 6.53033C8.17678 6.23744 8.17678 5.76256 8.46967 5.46967Z"
          fillRule="evenodd"
        />
      </svg>
    );
  };

  // Render status badge
  const renderStatusBadge = () => {
    if (!config.showStatusBadge) return null;

    return (
      <div
        style={{
          position: "absolute",
          top: "24px",
          right: "24px",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "2px",
        }}
      >
        <CaptionComponent className="font-semibold">
          {visit.status === "requested"
            ? "Requested"
            : visit.status.charAt(0).toUpperCase() + visit.status.slice(1)}
        </CaptionComponent>
        <CaptionComponent>{formattedTimestamp}</CaptionComponent>
      </div>
    );
  };

  // Render avatar - use custom component if provided, otherwise use default
  const renderAvatar = () => {
    const AvatarToRender = AvatarComponent || AvatarGroup;

    const avatarProps: any = {
      firstAvatar: visit.patientAvatarUrl || visit.patientName || "P",
      // For second avatar (clinician): pass URL if available, otherwise doctor icon URL
      secondAvatar: visit.clinicianAvatarUrl || DOCTOR_ICON_URL,
      layout: config.avatarLayout,
      size: "medium",
      theme: theme?.avatarGroup,
    };

    return <AvatarToRender {...avatarProps} />;
  };

  // Render main content based on layout
  const renderContent = () => {
    if (config.layout === "horizontal") {
      // Horizontal layout (like completed visits in Figma)
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          {/* Left: Avatar group */}
          <div style={{ flexShrink: 0, position: "relative" }}>
            {renderAvatar()}
          </div>

          {/* Center: Title and timestamp */}
          <div style={{ flex: 1, marginLeft: "16px", marginRight: "16px" }}>
            <TitleComponent className="font-bold mb-1">{title}</TitleComponent>
            {formattedTimestamp && (
              <CaptionComponent>{formattedTimestamp}</CaptionComponent>
            )}
          </div>

          {/* Right: Chevron */}
          <div style={{ flexShrink: 0 }}>{renderChevron()}</div>
        </div>
      );
    }

    // Vertical layout (default)
    return (
      <>
        {/* Upper content with avatar group */}
        <div style={{ marginBottom: "16px" }}>{renderAvatar()}</div>

        {/* Content area with title, subtitle and chevron */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ flex: 1 }}>
            <TitleComponent className="font-bold mb-1">{title}</TitleComponent>
            {config.showSubtitle && (
              <SubtitleComponent>{subtitle}</SubtitleComponent>
            )}
          </div>
          {renderChevron()}
        </div>

        {/* Status badge */}
        {renderStatusBadge()}
      </>
    );
  };

  // Main card content
  const cardContent = (
    <div
      className={`base-visit-item ${visit.status} ${getContainerStyles()} ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
      role={shouldBeClickable ? "button" : undefined}
      tabIndex={shouldBeClickable ? 0 : undefined}
    >
      {renderContent()}
    </div>
  );

  // Wrap with notification card if needed
  if (showNotification) {
    return (
      <div className="base-visit-item-wrapper">
        {cardContent}
        {renderNotificationContent()}
      </div>
    );
  }

  return cardContent;
};
